import React, { useEffect, useState } from 'react';
import { fetchOppsQueue, OPPS_STAGE_CONFIG } from '../api/monday';

// ── Design tokens (matches Queue.js) ──────────────────────────────────────
const C = {
  indigo:        '#534AB7',
  indigoDark:    '#3C3489',
  indigoLight:   '#EEEDFE',
  teal:          '#1D9E75',
  tealLight:     '#E1F5EE',
  tealDark:      '#085041',
  amber:         '#633806',
  amberLight:    '#FAEEDA',
  bgPage:        '#f5f4f1',
  bgCard:        '#ffffff',
  bgSoft:        '#f0efe9',
  border:        '#e8e6e0',
  textPrimary:   '#111827',
  textSecondary: '#6b7280',
  textTertiary:  '#9ca3af',
  redLight:      '#FCEBEB',
  red:           '#791F1F',
};

const AVATAR_PALETTE = [
  { bg: C.indigoLight, color: C.indigoDark },
  { bg: C.tealLight,   color: C.tealDark   },
  { bg: C.amberLight,  color: C.amber      },
  { bg: '#F3F0FF',     color: '#5B21B6'    },
  { bg: '#FEF9C3',     color: '#A16207'    },
];

function getAvatarStyle(name) {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function getInitials(name) {
  const parts = (name || '').split(/[\s|]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return '?';
}

function getCol(item, colId) {
  return item.column_values?.find((c) => c.id === colId)?.text || '';
}

function getStageConfig(stage) {
  return OPPS_STAGE_CONFIG[stage] || { label: stage || '—', bg: '#f3f4f6', color: '#6b7280', group: 'new' };
}

function getSourceLabel(item) {
  const src = getCol(item, 'dropdown_mksa3qbw');
  if (!src) return null;
  // Shorten common labels
  const SHORT = {
    'Direct Inquiry (Phone or Email)': 'Direct Inquiry',
    'Website Brochure Downloads / Shares': 'Brochure Download',
    'Crexi Brochure Download': 'Crexi Brochure',
  };
  return SHORT[src] || src;
}

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    padding: '0 0 24px', backgroundColor: C.bgPage, minHeight: '100vh',
  },
  header: {
    padding: '20px 18px 16px',
    borderBottom: `1px solid ${C.border}`,
    backgroundColor: C.bgCard,
    marginBottom: '12px',
  },
  headerTop: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: '4px',
  },
  appTitle: {
    fontSize: '11px', fontWeight: '600', color: C.textTertiary,
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  pageTitle: { fontSize: '20px', fontWeight: '700', color: C.textPrimary, margin: '4px 0 2px' },
  subtitle:  { fontSize: '13px', color: C.textSecondary },

  statRow: { display: 'flex', gap: '8px', marginTop: '14px' },
  statTile: { flex: 1, borderRadius: '10px', padding: '10px 12px', textAlign: 'center' },
  statNum:  { fontSize: '20px', fontWeight: '700', lineHeight: 1, marginBottom: '3px' },
  statLabel:{ fontSize: '10px', fontWeight: '600', letterSpacing: '0.04em' },

  section: { padding: '0 12px', marginBottom: '4px' },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 6px 6px',
  },
  sectionLabel: {
    fontSize: '11px', fontWeight: '700',
    letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  sectionBadge: {
    fontSize: '11px', fontWeight: '700',
    borderRadius: '99px', padding: '1px 8px',
  },

  card: {
    backgroundColor: C.bgCard, borderRadius: '12px',
    padding: '14px 16px', marginBottom: '6px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '12px',
    border: `1.5px solid transparent`,
  },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '600', flexShrink: 0,
  },
  cardContent: { flex: 1, minWidth: 0 },
  cardName:    { fontSize: '14px', fontWeight: '600', color: C.textPrimary, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta:    { fontSize: '11px', color: C.textTertiary, margin: '0 0 5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tag: {
    display: 'inline-block', fontSize: '11px', fontWeight: '500',
    padding: '2px 8px', borderRadius: '10px', marginRight: '4px',
  },

  emptyWrap:  { padding: '12px' },
  emptyCard:  { textAlign: 'center', padding: '48px 20px', backgroundColor: C.bgCard, borderRadius: '14px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  emptyIcon:  { fontSize: '36px', marginBottom: '10px' },
  emptyTitle: { fontSize: '17px', fontWeight: '700', color: C.textPrimary, margin: '0 0 6px' },
  emptyText:  { fontSize: '13px', color: C.textTertiary },
  loading:    { textAlign: 'center', padding: '60px 20px', color: C.textTertiary, fontSize: '15px' },
  errorBox:   { margin: '16px', background: C.redLight, border: `1px solid #FECACA`, borderRadius: '10px', padding: '14px', color: C.red, fontSize: '13px' },
};

// ── OppCard ────────────────────────────────────────────────────────────────
function OppCard({ item, onClick }) {
  const stage    = getCol(item, 'status');
  const company  = getCol(item, 'lookup_mkqs58br');
  const source   = getSourceLabel(item);
  const stageCfg = getStageConfig(stage);
  const av       = getAvatarStyle(item.name);

  // Extract first name from item name (name is contact's name)
  const displayName = item.name || '—';

  return (
    <div style={s.card} onClick={() => onClick(item)}>
      <div style={{ ...s.avatar, backgroundColor: av.bg, color: av.color }}>
        {getInitials(displayName)}
      </div>
      <div style={s.cardContent}>
        <p style={s.cardName}>{displayName}</p>
        <p style={s.cardMeta}>{company || '—'}</p>
        <div>
          <span style={{ ...s.tag, backgroundColor: stageCfg.bg, color: stageCfg.color }}>
            {stageCfg.label}
          </span>
          {source && (
            <span style={{ ...s.tag, backgroundColor: C.bgSoft, color: C.textSecondary }}>
              {source}
            </span>
          )}
        </div>
      </div>
      <div style={{ color: C.textTertiary, fontSize: '18px' }}>›</div>
    </div>
  );
}

// ── Section ────────────────────────────────────────────────────────────────
function Section({ label, emoji, items, badgeBg, badgeColor, onSelect }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!items.length) return null;

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <span style={{ fontSize: '14px' }}>{emoji}</span>
        <span style={{ ...s.sectionLabel, color: badgeColor }}>{label}</span>
        <span style={{ ...s.sectionBadge, backgroundColor: badgeBg, color: badgeColor }}>
          {items.length}
        </span>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: C.textTertiary, padding: 0, marginLeft: 'auto' }}
          onClick={() => setCollapsed((v) => !v)}>
          {collapsed ? '▸' : '▾'}
        </button>
      </div>
      {!collapsed && items.map((item) => (
        <OppCard key={item.id} item={item} onClick={onSelect} />
      ))}
    </div>
  );
}

// ── Cache helpers ──────────────────────────────────────────────────────────

const OPPS_CACHE_KEY = (broker) => `sterling_opps_v1_${broker}`;

function readOppsCache(broker) {
  try {
    const raw = localStorage.getItem(OPPS_CACHE_KEY(broker));
    return raw ? JSON.parse(raw).data : null;
  } catch { return null; }
}

function writeOppsCache(broker, data) {
  try { localStorage.setItem(OPPS_CACHE_KEY(broker), JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// ── Main component ─────────────────────────────────────────────────────────
export default function OppsQueue({ brokerName, onSelectItem }) {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');

  useEffect(() => {
    const cached = readOppsCache(brokerName);
    if (cached) {
      setData(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    fetchOppsQueue(brokerName)
      .then((d) => {
        setData(d);
        setLoading(false);
        setRefreshing(false);
        writeOppsCache(brokerName, d);
      })
      .catch((e) => {
        if (!cached) setError(e.message);
        setLoading(false);
        setRefreshing(false);
      });
  }, [brokerName]); // eslint-disable-line

  const q = search.trim().toLowerCase();
  const filterSearch = (items) => q ? items.filter((item) => item.name.toLowerCase().includes(q) || getCol(item, 'lookup_mkqs58br').toLowerCase().includes(q)) : items;
  const filteredData = data ? {
    newLeads:   filterSearch(data.newLeads),
    inSequence: filterSearch(data.inSequence),
    connected:  filterSearch(data.connected),
    closed:     filterSearch(data.closed),
  } : null;
  const total = data ? (data.newLeads.length + data.inSequence.length + data.connected.length + data.closed.length) : 0;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTop}>
          <span style={s.appTitle}>SterlingCRE</span>
        </div>
        <p style={s.pageTitle}>New Opportunities</p>
        <p style={s.subtitle}>
          {brokerName}
          {refreshing && <span style={{ marginLeft: '8px', fontSize: '11px', color: C.textTertiary }}>↻ syncing…</span>}
        </p>

        {data && (
          <div style={s.statRow}>
            <div style={{ ...s.statTile, backgroundColor: C.indigoLight }}>
              <div style={{ ...s.statNum, color: C.indigoDark }}>{total}</div>
              <div style={{ ...s.statLabel, color: C.indigoDark }}>Total Opps</div>
            </div>
            <div style={{ ...s.statTile, backgroundColor: C.tealLight }}>
              <div style={{ ...s.statNum, color: C.tealDark }}>{data.connected.length}</div>
              <div style={{ ...s.statLabel, color: C.tealDark }}>Connected</div>
            </div>
            <div style={{ ...s.statTile, backgroundColor: C.bgSoft }}>
              <div style={{ ...s.statNum, color: C.textSecondary }}>{data.newLeads.length}</div>
              <div style={{ ...s.statLabel, color: C.textSecondary }}>New Leads</div>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      {loading && <div style={s.loading}>Loading opportunities…</div>}
      {error   && <div style={s.errorBox}><strong>Error:</strong> {error}</div>}

      {data && !loading && total === 0 && (
        <div style={s.emptyWrap}>
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}>🏢</div>
            <p style={s.emptyTitle}>No opportunities</p>
            <p style={s.emptyText}>No active opps assigned to you right now.</p>
          </div>
        </div>
      )}

      {/* Search bar */}
      {data && (
        <div style={{ padding: '10px 12px 0' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: C.textTertiary, pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              placeholder="Search opportunities…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 12px 10px 36px',
                border: `1.5px solid ${C.border}`, borderRadius: '10px',
                fontSize: '14px', color: C.textPrimary,
                backgroundColor: C.bgCard, fontFamily: 'inherit', outline: 'none',
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: C.textTertiary, lineHeight: 1 }}>×</button>
            )}
          </div>
        </div>
      )}

      {filteredData && (
        <>
          <Section
            label="New Leads"
            emoji="🆕"
            items={filteredData.newLeads}
            badgeBg={C.bgSoft}
            badgeColor={C.textSecondary}
            onSelect={onSelectItem}
          />
          <Section
            label="In Sequence"
            emoji="📧"
            items={filteredData.inSequence}
            badgeBg={C.indigoLight}
            badgeColor={C.indigoDark}
            onSelect={onSelectItem}
          />
          <Section
            label="Connected"
            emoji="🤝"
            items={filteredData.connected}
            badgeBg={C.tealLight}
            badgeColor={C.tealDark}
            onSelect={onSelectItem}
          />
          {filteredData.closed.length > 0 && (
            <Section
              label="Closed"
              emoji="📁"
              items={filteredData.closed}
              badgeBg="#f3f4f6"
              badgeColor={C.textSecondary}
              onSelect={onSelectItem}
            />
          )}
          {search && (filteredData.newLeads.length + filteredData.inSequence.length + filteredData.connected.length + filteredData.closed.length) === 0 && (
            <div style={s.emptyWrap}>
              <div style={s.emptyCard}>
                <div style={s.emptyIcon}>🔍</div>
                <p style={s.emptyTitle}>No results</p>
                <p style={s.emptyText}>No opportunities match "{search}"</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
